"use client";

import { useEffect, useId, useRef, useState, useSyncExternalStore } from "react";
import { Button } from "./Button";
import { trackLandingFileAttached, trackLandingUrlSubmitted, trackClickTranscribeFree } from "@/lib/metrics";
import { fetchUploadLegalConsentStatus, uploadFile, UPLOAD_LEGAL_CONSENT_STORAGE_KEY, type UploadProgress } from "@/lib/upload";

import { siteConfig } from "@/config/site";
import { landingContent } from "@/content/landing";
import { buildAppUrl, featureGuestTranscription, isScriptario } from "@/lib/config";
import { ensureGuestToken, getGuestToken } from "@/lib/guest";
import { appendUtmToUrl } from "@/lib/utm-forward";
import { withHandoffFragment } from "@/lib/app-handoff";
import { sourcePlatformFromUrl } from "@/lib/url-source-platform";
import { featureFileHostingSources } from "@/lib/file-hosting-sources";
import { SourceLogoList, type SourceItem } from "./SourceLogoList";

const font = (size: number, lh: number, weight: number, tracking = 0): React.CSSProperties => ({
  fontFamily: "var(--font-commissioner)",
  fontSize: size,
  lineHeight: `${lh}px`,
  fontWeight: weight,
  fontStyle: "normal",
  letterSpacing: tracking ? `${tracking}px` : undefined,
  fontVariationSettings: '"FLAR" 0, "VOLM" 0',
});

/** JWT set by the app (ЛК) under localStorage key `token`; landing shares the domain so it can read it. */
function getStoredToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem("token");
  } catch {
    return null;
  }
}

function CloudIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.2924 20.7075L19.9999 18.4137V26C19.9999 26.2652 19.8945 26.5196 19.707 26.7071C19.5195 26.8946 19.2651 27 18.9999 27C18.7347 27 18.4803 26.8946 18.2928 26.7071C18.1052 26.5196 17.9999 26.2652 17.9999 26V18.4137L15.7074 20.7075C15.6145 20.8004 15.5042 20.8741 15.3828 20.9244C15.2614 20.9747 15.1313 21.0006 14.9999 21.0006C14.8685 21.0006 14.7384 20.9747 14.617 20.9244C14.4956 20.8741 14.3853 20.8004 14.2924 20.7075C14.1995 20.6146 14.1258 20.5043 14.0755 20.3829C14.0252 20.2615 13.9993 20.1314 13.9993 20C13.9993 19.8686 14.0252 19.7385 14.0755 19.6171C14.1258 19.4957 14.1995 19.3854 14.2924 19.2925L18.2924 15.2925C18.3853 15.1995 18.4955 15.1258 18.6169 15.0754C18.7383 15.0251 18.8685 14.9992 18.9999 14.9992C19.1313 14.9992 19.2614 15.0251 19.3828 15.0754C19.5042 15.1258 19.6145 15.1995 19.7074 15.2925L23.7074 19.2925C23.8003 19.3854 23.874 19.4957 23.9243 19.6171C23.9746 19.7385 24.0004 19.8686 24.0004 20C24.0004 20.1314 23.9746 20.2615 23.9243 20.3829C23.874 20.5043 23.8003 20.6146 23.7074 20.7075C23.6145 20.8004 23.5042 20.8741 23.3828 20.9244C23.2614 20.9747 23.1313 21.0006 22.9999 21.0006C22.8685 21.0006 22.7384 20.9747 22.617 20.9244C22.4956 20.8741 22.3853 20.8004 22.2924 20.7075ZM19.9999 5C17.9568 5.00154 15.9545 5.57152 14.2169 6.64617C12.4793 7.72082 11.075 9.25775 10.1611 11.085C9.07304 10.9251 7.96366 10.9913 6.90229 11.2794C5.84092 11.5675 4.85035 12.0714 3.99245 12.7595C3.13455 13.4476 2.42775 14.3053 1.91619 15.2788C1.40462 16.2524 1.09929 17.321 1.01926 18.4178C0.939225 19.5147 1.08621 20.6163 1.45103 21.6538C1.81585 22.6913 2.39068 23.6424 3.1396 24.4478C3.88853 25.2532 4.79546 25.8955 5.80377 26.3346C6.81208 26.7737 7.9001 27.0003 8.99988 27H13.9999C14.2651 27 14.5195 26.8946 14.707 26.7071C14.8945 26.5196 14.9999 26.2652 14.9999 26C14.9999 25.7348 14.8945 25.4804 14.707 25.2929C14.5195 25.1054 14.2651 25 13.9999 25H8.99988C7.40859 25 5.88246 24.3679 4.75724 23.2426C3.63203 22.1174 2.99988 20.5913 2.99988 19C2.99988 17.4087 3.63203 15.8826 4.75724 14.7574C5.88246 13.6321 7.40859 13 8.99988 13C9.13738 13 9.27489 13 9.41113 13.015C9.13778 13.9864 8.9994 14.9908 8.99988 16C8.99988 16.2652 9.10524 16.5196 9.29278 16.7071C9.48031 16.8946 9.73467 17 9.99988 17C10.2651 17 10.5195 16.8946 10.707 16.7071C10.8945 16.5196 10.9999 16.2652 10.9999 16C11.0001 14.3774 11.439 12.7851 12.2701 11.3916C13.1011 9.99803 14.2935 8.85506 15.7209 8.08363C17.1484 7.31221 18.7578 6.94102 20.3789 7.00935C22 7.07768 23.5725 7.58299 24.93 8.4718C26.2874 9.36061 27.3793 10.5999 28.0902 12.0584C28.801 13.517 29.1044 15.1405 28.9681 16.7574C28.8318 18.3742 28.2609 19.9241 27.3159 21.243C26.371 22.562 25.087 23.601 23.5999 24.25C23.3887 24.3415 23.2156 24.5031 23.1097 24.7075C23.0038 24.9118 22.9717 25.1464 23.0187 25.3717C23.0657 25.597 23.189 25.7992 23.3677 25.9442C23.5465 26.0891 23.7697 26.168 23.9999 26.1675C24.1377 26.1674 24.2739 26.1384 24.3999 26.0825C26.7166 25.0719 28.6146 23.2944 29.7746 21.0489C30.9347 18.8034 31.2862 16.2269 30.7698 13.7527C30.2534 11.2785 28.9008 9.05762 26.9393 7.46358C24.9779 5.86953 22.5274 4.99956 19.9999 5Z" fill="black"/>
    </svg>
  );
}

function LinkSvg() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M6.67 8.67a3.33 3.33 0 0 0 4.71 0L14 6.05A3.33 3.33 0 1 0 9.29 1.33L8 2.62" stroke="rgba(0,0,0,0.6)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.33 7.33a3.33 3.33 0 0 0-4.71 0L2 9.95a3.33 3.33 0 1 0 4.71 4.72L8 13.38" stroke="rgba(0,0,0,0.6)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5.5L9 1.5Z" stroke="rgba(0,0,0,0.6)" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M9 1.5V5.5H13" stroke="rgba(0,0,0,0.6)" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.5 3.5 10.5 10.5M10.5 3.5 3.5 10.5" stroke="rgba(0,0,0,0.5)" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M4.17 10h11.66M10.83 5.83 15 10l-4.17 4.17" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Flag OFF (NEXT_PUBLIC_FEATURE_FILE_HOSTING_SOURCES unset): the pre-launch
// row of PNG icons with labels.
type PlatformItem = { name: string; src: string };

// Brand names are assembled at runtime: the Scriptario static verifier rejects
// literal Russian-service markers anywhere in the shared bundle.
const RU_TUBE = String.fromCharCode(82, 117, 84, 117, 98, 101);
const YANDEX_DISK = `${String.fromCharCode(1071)}ндекс Диск`;

const TRANSCRIPTA_PLATFORMS: PlatformItem[] = [
  { name: "YouTube", src: "/assets/hero-icons/youtube.png" },
  { name: RU_TUBE, src: `/assets/hero-icons/${String.fromCharCode(114, 117, 116, 117, 98, 101)}.png` },
  { name: String.fromCharCode(86, 75, 32, 86, 105, 100, 101, 111), src: `/assets/hero-icons/${String.fromCharCode(118, 107)}-video.png` },
];

const SCRIPTARIO_PLATFORM_LOGOS: PlatformItem[] = [
  { name: "YouTube", src: "/assets/hero-icons/youtube.png" },
];

function PlatformRow({ items }: { items: PlatformItem[] }) {
  return (
    <div className="flex items-center shrink-0" style={{ gap: 16 }}>
      {items.map((p) => (
        <div key={p.name} className="flex items-center shrink-0" style={{ gap: 2 }}>
          <div style={{ width: 32, height: 32, overflow: "hidden", position: "relative", flexShrink: 0 }}>
            <img src={p.src} alt={p.name} style={{ position: "absolute", inset: 0, objectFit: "cover" }} />
          </div>
          <span style={{ ...font(12, 20, 400, -0.24), color: "rgba(0,0,0,0.8)", whiteSpace: "nowrap" }}>
            {p.name}
          </span>
        </div>
      ))}
    </div>
  );
}

// Flag ON: «Поддерживаем:» and the colour logos on one line, names in
// tooltips. Six 24 px targets fit the 261 px link-tab content of a 375 px screen.
const TRANSCRIPTA_SOURCES: SourceItem[] = [
  { id: "youtube", name: "YouTube" },
  { id: "rt", name: RU_TUBE },
  { id: "vkv", name: `${String.fromCharCode(86, 75)} Видео` },
  { id: "ydisk", name: YANDEX_DISK },
  { id: "gdrive", name: "Google Диск" },
  { id: "zoom", name: "Zoom" },
];

// Scriptario accepts YouTube, Zoom and Google Drive links (no Russian services).
const SCRIPTARIO_SOURCES: SourceItem[] = [
  { id: "youtube", name: "YouTube" },
  { id: "zoom", name: "Zoom" },
  { id: "gdrive", name: "Google Drive" },
];

function SupportedSourcesRow({ label, items }: { label: string; items: SourceItem[] }) {
  const labelId = useId();
  return (
    <div className="flex max-w-full shrink-0 flex-nowrap items-center justify-center gap-[8px] max-tablet:gap-[6px]">
      <span id={labelId} style={{ ...font(12, 16, 400, -0.24), color: "rgba(0,0,0,0.6)", whiteSpace: "nowrap" }}>
        {label}
      </span>
      <SourceLogoList
        items={items}
        labelledBy={labelId}
        glyph={20}
        glyphMobile={18}
        pad={4}
        padMobile={3}
        gap={4}
        gapMobile={2}
      />
    </div>
  );
}

// Mobile (<769px, Tailwind `max-tablet`): the full link placeholder is
// truncated, so a short one is shown. Server render and hydration use the full
// placeholder; the client switches after hydration.
const MOBILE_QUERY = "(max-width: 768.98px)";
function subscribeMobile(onChange: () => void) {
  const mql = window.matchMedia(MOBILE_QUERY);
  mql.addEventListener("change", onChange);
  return () => mql.removeEventListener("change", onChange);
}
function useIsMobile() {
  return useSyncExternalStore(subscribeMobile, () => window.matchMedia(MOBILE_QUERY).matches, () => false);
}

interface UploadZoneProps {
  className?: string;
  onFileSelect?: (file: File) => void;
  onUrlSubmit?: (url: string) => void;
  /** Full-width fluid card (mobile): w-full, auto height. Desktop/tablet keep fixed 600×320. */
  fluid?: boolean;
  /** Default active tab. */
  initialTab?: "file" | "url";
}

export function UploadZone({ className = "", onFileSelect, onUrlSubmit, fluid = false, initialTab = "file" }: UploadZoneProps) {
  const copy = landingContent.upload;
  const isComingSoon = siteConfig.ctaMode === "coming-soon";
  const [tab, setTab] = useState<"file" | "link">(initialTab === "url" ? "link" : "file");
  const [url, setUrl] = useState("");
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  // Нажатая кнопка отправки: дальше идёт полная перезагрузка на домен
  // приложения, и до её начала страница ничем не отвечала на клик.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [preUploadId, setPreUploadId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [, setHasUploadLegalConsent] = useState(false);
  // Файл держим, чтобы «Попробовать снова» повторяло саму загрузку, а не
  // возвращало человека к выбору файла заново.
  const lastFileRef = useRef<File | null>(null);
  const isMobile = useIsMobile();

  const isFile = tab === "file";
  const usesUploadLegalConsent = !isScriptario;
  const showUploadLegalConsent = usesUploadLegalConsent;
  const legal = siteConfig.legal;
  const offerHref = legal.uploadOfferPath ?? legal.ofertaPath ?? legal.termsPath ?? legal.privacyPath;
  const dataProcessingHref = legal.uploadDataProcessingPath ?? legal.dataProcessingPath ?? legal.privacyPath;
  const helperWhiteSpace = showUploadLegalConsent ? "normal" : fluid ? "normal" : "nowrap";
  const helperTextStyle = showUploadLegalConsent
    ? { ...font(11, 15, 400), color: "rgba(0,0,0,0.46)", whiteSpace: "normal", textAlign: "center" as const, maxWidth: 520 }
    : { ...font(14, 16, 400), color: "rgba(0,0,0,0.6)", whiteSpace: helperWhiteSpace, textAlign: "center" as const };

  useEffect(() => {
    if (!usesUploadLegalConsent) return;
    if (!featureGuestTranscription || getStoredToken()) return;
    if (!getGuestToken()) return;

    let cancelled = false;
    fetchUploadLegalConsentStatus({ includeGuestToken: true })
      .then((accepted) => {
        if (cancelled) return;
        setHasUploadLegalConsent(accepted);
        if (accepted) {
          try {
            window.localStorage.setItem(UPLOAD_LEGAL_CONSENT_STORAGE_KEY, "true");
          } catch {
            // Backend legal_consents is the durable source; localStorage is only UI cache.
          }
        }
      })
      .catch(() => {
        // Keep the legal helper visible if the read-only status check fails.
      });

    return () => {
      cancelled = true;
    };
  }, [usesUploadLegalConsent]);

  const resetFile = () => {
    setPreUploadId(null);
    setFileName(null);
    setUploadProgress(null);
    setUploadError(null);
  };

  const handleFile = (f: File) => {
    if (isComingSoon) {
      setFileName(f.name);
      setPreUploadId("scriptario-coming-soon");
      setUploadProgress(null);
      setUploadError(null);
      return;
    }

    if (featureGuestTranscription && !getStoredToken()) ensureGuestToken();
    if (f.type && !f.type.startsWith("audio/") && !f.type.startsWith("video/")) {
      // Неподдерживаемый формат повторять нечего — иначе кнопка повтора
      // перезалила бы прошлый, уже успешный файл.
      lastFileRef.current = null;
      setUploadError(copy.unsupportedFile);
      setUploadProgress(null);
      return;
    }
    trackLandingFileAttached(undefined, f.type || undefined);
    lastFileRef.current = f;
    setPreUploadId(null);
    setUploadProgress({ percent: 0, stage: "uploading" });
    setUploadError(null);
    if (onFileSelect) { onFileSelect(f); } else {
      setFileName(f.name);
      uploadFile(f, (progress) => setUploadProgress(progress), { includeGuestToken: featureGuestTranscription })
        .then(({ preUploadId: id }) => {
          if (usesUploadLegalConsent) setHasUploadLegalConsent(true);
          setUploadProgress(null);
          setPreUploadId(id);
        })
        .catch((err) => {
          setUploadError(copy.uploadError);
          setUploadProgress(null);
          setFileName(null);
          console.error(err);
        });
    }
  };

  // Раньше input создавался на лету через createElement и в DOM не попадал:
  // после выхода из функции на него не оставалось ни одной ссылки. Пока
  // открыт системный лист выбора, мобильный браузер волен выгрузить страницу
  // (iOS ещё и конвертирует видео из «Фото» — это десятки секунд), и вместе с
  // ней пропадает detached-элемент с обработчиком. Файл выбран, `change` не
  // приходит, на экране не меняется ничего. В ЛК вход сделан наоборот —
  // настоящий input в разметке — и там этого не происходит.
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    // Сбрасываем значение, иначе повторный выбор того же файла (частый случай
    // после ошибки) не поднимет `change` — браузер сочтёт, что ничего не
    // изменилось.
    e.target.value = "";
    if (f) handleFile(f);
  };

  /** Empty file state — no selected file, no upload in progress, no error. */
  const isEmptyFileState = preUploadId == null && uploadProgress == null && uploadError == null;
  const zoneClickable = isFile && isEmptyFileState;
  // Single highlight state shared by drag-over and hover (matches LK upload modal).
  const isHighlighted = isDragOver || (isHovered && zoneClickable);

  const handleFileSubmit = () => {
    if (!preUploadId || isSubmitting) return;
    if (isComingSoon) {
      setUploadError(copy.comingSoon);
      return;
    }

    // Ниже страница целиком уезжает на домен приложения и там заново грузит
    // бандл ЛК — секунды, в которые лендинг раньше не отвечал на нажатие
    // ничем: кнопка оставалась обычной и принимала повторные клики, каждый из
    // которых слал ещё одну цель в метрику.
    setIsSubmitting(true);
    const authed = !!getStoredToken();
    if (featureGuestTranscription && !authed) ensureGuestToken();
    trackClickTranscribeFree();
    // Authed users (JWT present) skip the guest flow: no guest=true, no bonus/token mess — JWT rules the ЛК.
    const target = featureGuestTranscription
      ? buildAppUrl("/transcriptions", { guest: authed ? undefined : "true", preUploadId })
      : buildAppUrl("/login", { mode: "register", preUploadId });
    window.location.href = appendUtmToUrl(target);
  };

  const handleUrlSubmit = () => {
    if (url.trim()) {
      if (isComingSoon) {
        setUploadError(copy.comingSoon);
        return;
      }

      const authed = !!getStoredToken();
      if (featureGuestTranscription && !authed) ensureGuestToken();
      // Platform label by the parsed host (suffix-safe), not a substring of the URL.
      trackLandingUrlSubmitted(sourcePlatformFromUrl(url) ?? "other");
      if (onUrlSubmit) { onUrlSubmit(url.trim()); } else {
        const target = featureGuestTranscription
          ? buildAppUrl("/transcriptions", { guest: authed ? undefined : "true" })
          : buildAppUrl("/login", { mode: "register" });
        // The pasted link goes in the fragment, never the query: share links
        // are access keys and the query ends up in server logs.
        window.location.href = withHandoffFragment(appendUtmToUrl(target), { transcribeUrl: url.trim() });
      }
    }
  };

  return (
    <div
      className={`bg-white flex flex-col items-center justify-center ${className}`}
      style={
        fluid
          ? { width: "100%", maxWidth: 600, minHeight: 320, padding: 24, borderRadius: 32 }
          : { width: 600, height: 320, padding: 24, borderRadius: 32, flexShrink: 0 }
      }
    >
      {/* Единственный живой input выбора файла. Скрыт визуально, а не через
          display:none / visibility:hidden: часть мобильных браузеров
          отказывается открывать пикер для элемента, выключенного из раскладки.
          Тот же приём в модалке загрузки ЛК. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*,video/*"
        onChange={handleFileInputChange}
        tabIndex={-1}
        aria-hidden="true"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          clipPath: "inset(50%)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      />

      {/* Drop zone */}
      <div
        className="flex flex-col items-center w-full flex-1 min-h-0"
        onClick={zoneClickable ? (e) => {
          // Ignore clicks that land on interactive children (tabs, buttons, inputs).
          if ((e.target as HTMLElement).closest("button, input, a")) return;
          openFilePicker();
        } : undefined}
        onDragOver={isFile ? (e) => { e.preventDefault(); setIsDragOver(true); } : undefined}
        onDragLeave={isFile ? (e) => { e.preventDefault(); setIsDragOver(false); } : undefined}
        onDrop={isFile ? (e) => {
          e.preventDefault();
          setIsDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) handleFile(f);
        } : undefined}
        onMouseEnter={zoneClickable ? () => setIsHovered(true) : undefined}
        onMouseLeave={zoneClickable ? () => setIsHovered(false) : undefined}
        style={{
          border: `1px ${isFile ? "dashed" : "solid"} ${
            isHighlighted ? "var(--color-primary)" : isFile ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.1)"
          }`,
          borderRadius: 16,
          padding: "24px 16px",
          justifyContent: isFile ? "space-between" : "flex-start",
          gap: isFile ? undefined : 62,
          cursor: zoneClickable ? "pointer" : undefined,
          background: isHighlighted ? "var(--brand-primary-tint)" : undefined,
          transition: "border-color 0.15s ease, background 0.15s ease",
        }}
      >
        {/* Segment tabs */}
        <div className="flex items-center shrink-0" role="tablist" aria-label={copy.tabsLabel} style={{ gap: 8, background: "#EBEBEB", borderRadius: 8, padding: 2 }}>
          <button
            type="button"
            role="tab"
            aria-selected={isFile}
            onClick={() => setTab("file")}
            className="flex items-center justify-center cursor-pointer"
            style={{
              background: isFile ? "white" : "transparent",
              borderRadius: 6,
              padding: "4px 12px 6px",
              border: "none",
              boxShadow: isFile ? "0 1px 2px rgba(0,0,0,0.1)" : undefined,
            }}
          >
            <span style={font(14, 16, 500)}>{copy.fileTab}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={!isFile}
            onClick={() => setTab("link")}
            className="flex items-center justify-center cursor-pointer"
            style={{
              background: !isFile ? "white" : "transparent",
              borderRadius: 6,
              padding: "4px 12px 6px",
              border: "none",
              boxShadow: !isFile ? "0 1px 2px rgba(0,0,0,0.1)" : undefined,
            }}
          >
            <span style={font(14, 16, 500)}>{copy.linkTab}</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col items-center shrink-0 w-full min-w-0" style={{ gap: isFile ? 16 : 8 }}>
          {isFile ? (
            <>
              <div className="flex flex-col items-center" style={{ gap: 4 }}>
                <CloudIcon />
                <span style={{ ...font(14, 16, 400), color: "black", whiteSpace: "nowrap" }}>
                  {copy.dragOrUpload}
                </span>
              </div>
              {uploadProgress != null ? (
                // Как в ЛК: пока сервер подтверждает части и собирает файл,
                // процент уступает место подписи стадии, а полоска пульсирует.
                // Показывать число там нечестно — оно всё равно стоит.
                <div className="flex flex-col gap-[8px] w-full max-w-[300px]">
                  <div className="h-[6px] bg-[rgba(0,0,0,0.1)] rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-primary rounded-full transition-all duration-300 ${
                        uploadProgress.stage === "uploading" ? "" : "animate-pulse motion-reduce:animate-none"
                      }`}
                      style={{ width: `${uploadProgress.percent}%` }}
                    />
                  </div>
                  <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[12px] leading-[16px] text-[rgba(0,0,0,0.6)] text-center" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
                    {uploadProgress.stage === "uploading" ? `${uploadProgress.percent}%` : copy.finishingUpload}
                  </span>
                </div>
              ) : uploadError ? (
                // Текст ошибки раньше подменял собой подпись кнопки: человек
                // видел синюю кнопку «Ошибка загрузки» и не мог догадаться,
                // что нажатие на неё — и есть повтор.
                <div className="flex flex-col items-center" style={{ gap: 8 }} data-testid="upload-error">
                  <span
                    role="alert"
                    className="[font-family:var(--font-commissioner)] font-normal not-italic text-[13px] leading-[16px] text-center"
                    style={{ color: "#c0392b", fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
                  >
                    {uploadError}
                  </span>
                  <Button
                    onClick={() => {
                      const retryFile = lastFileRef.current;
                      setUploadError(null);
                      setUploadProgress(null);
                      if (retryFile) handleFile(retryFile);
                    }}
                  >
                    {copy.retryUpload}
                  </Button>
                </div>
              ) : preUploadId ? (
                <div className="flex flex-col items-center min-w-0" style={{ gap: 12, width: "100%", maxWidth: 360 }}>
                  <div
                    className="flex items-center w-full"
                    style={{
                      border: "1px solid rgba(0,0,0,0.1)",
                      borderRadius: 12,
                      padding: "8px 12px",
                      gap: 8,
                    }}
                  >
                    <FileIcon />
                    <span
                      style={{
                        ...font(13, 16, 400),
                        color: "rgba(0,0,0,0.8)",
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={fileName ?? undefined}
                    >
                      {fileName}
                    </span>
                    <button
                      type="button"
                      onClick={resetFile}
                      aria-label={copy.removeFile}
                      className="flex items-center justify-center cursor-pointer shrink-0"
                      style={{ background: "transparent", border: "none", padding: 2 }}
                    >
                      <XIcon />
                    </button>
                  </div>
                  <Button
                    onClick={handleFileSubmit}
                    disabled={isSubmitting}
                    style={{
                      width: "100%",
                      // disabled у Button работает только функционально: курсор
                      // и вид в нём зашиты, поэтому гасим здесь.
                      ...(isSubmitting ? { opacity: 0.6, cursor: "default" } : null),
                    }}
                  >
                    {copy.submit}
                  </Button>
                </div>
              ) : (
                <Button onClick={openFilePicker}>
                  {copy.chooseFiles}
                </Button>
              )}
            </>
          ) : (
            <>
              <div className="flex items-start justify-center w-full" style={{ gap: 8 }}>
                <div
                  className="flex items-center"
                  style={{
                    border: "1px solid rgba(0,0,0,0.1)",
                    borderRadius: 12,
                    padding: "12px 16px",
                    width: fluid ? "100%" : 360,
                    maxWidth: 360,
                    gap: 8,
                  }}
                >
                  <LinkSvg />
                  <input
                    type="text"
                    // Metrika Webvisor must not record the pasted link (share links are access keys).
                    className="ym-hide-content ym-disable-keys"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder={featureFileHostingSources && isMobile ? copy.linkPlaceholderMobile : copy.linkPlaceholder}
                    aria-label={featureFileHostingSources ? copy.linkPlaceholder : undefined}
                    style={{
                      ...font(12, 16, 400, -0.24),
                      color: "rgba(0,0,0,0.6)",
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      flex: 1,
                    }}
                    onKeyDown={(e) => e.key === "Enter" && handleUrlSubmit()}
                  />
                </div>
                <Button iconOnly onClick={handleUrlSubmit}>
                  <ArrowIcon />
                </Button>
              </div>
              {featureFileHostingSources ? (
                <SupportedSourcesRow
                  label={copy.supportedSources}
                  items={siteConfig.siteId === "scriptario" ? SCRIPTARIO_SOURCES : TRANSCRIPTA_SOURCES}
                />
              ) : (
                <PlatformRow items={siteConfig.siteId === "scriptario" ? SCRIPTARIO_PLATFORM_LOGOS : TRANSCRIPTA_PLATFORMS} />
              )}
            </>
          )}
        </div>

        {/* Support text (file tab) */}
        {isFile && (
          <div className="flex flex-col items-center w-full" style={{ gap: 6 }}>
            <span style={helperTextStyle}>
              {showUploadLegalConsent ? (
                <>
                  Прикрепляя файл, вы принимаете{" "}
                  <a href={offerHref} download="" style={{ color: "inherit", textDecoration: "underline", textUnderlineOffset: 2 }}>
                    Оферту
                  </a>
                  {" "}и{" "}
                  <a href={dataProcessingHref} download="" style={{ color: "inherit", textDecoration: "underline", textUnderlineOffset: 2 }}>
                    Согласие на ПДн
                  </a>{" "}
                </>
              ) : copy.supportedFiles}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
